import { useEffect, useState } from "react";
import Modal from "@/components/base/Modal";
import {
  inputClass,
  labelClass,
  ghostButtonClass,
  primaryButtonClass,
} from "@/components/base/fieldStyles";
import type { KitItem } from "@/types";
import { uid } from "@/utils/format";

interface KitFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (item: KitItem) => void;
  editing: KitItem | null;
}

// Imagen sugerida por defecto para una pieza nueva (se puede reemplazar por
// cualquier URL de imagen propia).
const SUGGESTED_IMAGE =
  "https://readdy.ai/api/search-image?query=Clean%20minimal%20studio%20still%20life%20of%20a%20beauty%20and%20wellness%20product%20on%20a%20soft%20neutral%20cream%20background%20with%20warm%20natural%20light%2C%20elegant%20editorial%20aesthetic%2C%20soft%20shadows%2C%20high%20detail&width=800&height=800&seq=attd-kit-custom-01&orientation=squarish";

const KitFormModal = ({ open, onClose, onSubmit, editing }: KitFormModalProps) => {
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    setTitle(editing?.title ?? "");
    setText(editing?.text ?? "");
    setImageUrl(editing?.imageUrl ?? "");
  }, [open, editing]);

  const submit = () => {
    if (!title.trim()) {
      setError("Escribe un título para la pieza del kit.");
      return;
    }
    if (!text.trim()) {
      setError("Escribe el texto que el asesor podrá copiar y compartir.");
      return;
    }
    const item: KitItem = {
      id: editing?.id ?? uid("kit"),
      title: title.trim(),
      text: text.trim(),
      imageUrl: imageUrl.trim() || SUGGESTED_IMAGE,
      createdAt: editing?.createdAt ?? new Date().toISOString(),
    };
    onSubmit(item);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? "Editar pieza del kit" : "Nueva pieza del kit"}
      icon="ri-image-2-line"
      size="lg"
      footer={
        <>
          <button onClick={onClose} className={ghostButtonClass}>
            Cancelar
          </button>
          <button onClick={submit} className={primaryButtonClass}>
            <i className="ri-save-3-line" /> {editing ? "Guardar cambios" : "Publicar pieza"}
          </button>
        </>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-4">
          <div>
            <label className={labelClass}>Título</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ej: Promoción del mes"
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>Texto para compartir</label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              maxLength={500}
              rows={6}
              placeholder="Escribe la copia que el asesor copiará y enviará por WhatsApp o redes."
              className={`${inputClass} resize-none`}
            />
            <p className="text-[10px] text-foreground-500 mt-1">{text.length}/500</p>
          </div>

          <div>
            <label className={labelClass}>Imagen (URL)</label>
            <input
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="https://..."
              className={inputClass}
            />
            <button
              type="button"
              onClick={() => setImageUrl(SUGGESTED_IMAGE)}
              className="mt-2 text-[11px] font-semibold text-accent-200 hover:text-accent-300 cursor-pointer inline-flex items-center gap-1.5"
            >
              <i className="ri-magic-line" /> Usar imagen sugerida
            </button>
          </div>
        </div>

        <div>
          <label className={labelClass}>Vista previa</label>
          <div className="glass-panel rounded-lg overflow-hidden">
            <div className="w-full h-44 bg-background-200/40">
              {imageUrl.trim() || title.trim() ? (
                <img
                  src={imageUrl.trim() || SUGGESTED_IMAGE}
                  alt={title || "Vista previa"}
                  title={title || "Vista previa"}
                  className="w-full h-full object-cover object-top"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-foreground-500">
                  <i className="ri-image-line text-2xl" />
                </div>
              )}
            </div>
            <div className="p-4">
              <h3 className="editable-title text-sm font-bold">{title || "Título de la pieza"}</h3>
              <p className="text-[11px] md:text-xs text-foreground-600 mt-2">
                {text || "Aquí se verá el texto que el asesor podrá copiar y compartir."}
              </p>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <p className="text-[11px] text-primary-200 mt-3 flex items-center gap-1.5">
          <i className="ri-error-warning-line" /> {error}
        </p>
      )}
    </Modal>
  );
};

export default KitFormModal;